import { IUser } from 'core/api/users/users-api-interface';
import UsersApiService from 'core/api/users/users-api.service';
import { auth } from 'core/config/firebase';
import { Role } from 'core/config/roles';
import { onAuthStateChanged, User } from 'firebase/auth';
import { Unsubscribe } from 'firebase/firestore';
import { createContext, useContext, useEffect, useState } from 'react';

interface IAuthContext {
  user: User | null;
  error?: Error;
  authChecked: boolean;
  userRoles?: Role[];
  userData?: IUser;
}

export const AuthContext = createContext<IAuthContext | null>(null);

export const useAuthState = () => {
  const state = useContext(AuthContext);
  return { ...state, isAuthenticated: state?.user !== null };
};

export const AuthProvider = ({ children }: any) => {
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<IUser>();
  const [userRoles, setUserRoles] = useState<Role[]>();
  const [error, setError] = useState<Error>();
  const [authChecked, setAuthChecked] = useState<boolean>(false);

  useEffect(() => {
    const handleCurrentUserDocChanges = (doc: IUser) => {
      setUserData(doc);
    };

    const handleSubscriptionError = (error: any) => {
      console.log(error);
    };

    let unsubscribeFromUserDoc: Unsubscribe;

    const unsubscribeFromAuth = onAuthStateChanged(
      auth,
      async (user) => {
        if (user) {
          try {
            unsubscribeFromUserDoc = UsersApiService.subscribeToUser(
              user.uid,
              handleCurrentUserDocChanges,
              handleSubscriptionError,
            );
            const idTokenResult = await user.getIdTokenResult(true);
            let roles: Role[] = [];
            const legacyRolesClaim = idTokenResult.claims.role as Role;
            const rolesClaim = idTokenResult.claims.roles as Role[];
            if (!rolesClaim) {
              roles = [legacyRolesClaim];
            } else {
              roles = rolesClaim;
            }
            setUserRoles(roles);
          } catch (error) {
            setError(new Error('Failed to process user data.'));
          }
        }
        setUser(user);
        setTimeout(() => {
          setAuthChecked(true);
        }, 1000);
      },
      setError,
    );

    return () => {
      unsubscribeFromAuth();

      if (unsubscribeFromUserDoc) {
        unsubscribeFromUserDoc();
      }
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, error, authChecked, userRoles, userData }}>{children}</AuthContext.Provider>
  );
};
